/**
 * Utilities for converting Lexical editor data to clean text formats for AI analysis
 */

export interface LexicalEditorState {
  root: {
    children: Array<{
      children: Array<{
        text: string
        type: string
      }>
      type: string
      sectionType?: string
    }>
  }
}

export interface SectionedLyrics {
  sections: Array<{
    type: string
    lines: string[]
  }>
  plainText: string
  structuredText: string
}

// Type for stress patterns extracted from Lexical data
export interface StressPatternData {
  [lineKey: string]: unknown  // e.g., "line_0": { pattern: "...", syllables: 8 }
}

// Extended Lexical child node with stress pattern support
interface LexicalChildNode {
  text: string
  type: string
  stressPatterns?: unknown
}

/**
 * Extract clean text from Lexical editor state, preserving section structure
 * This removes all stress patterns, formatting, and editor metadata
 */
export function extractLyricsFromLexical(lexicalData: string | LexicalEditorState): SectionedLyrics {
  try {
    // Parse if string
    const editorState = typeof lexicalData === 'string'
      ? JSON.parse(lexicalData) as LexicalEditorState
      : lexicalData

    const sections: Array<{ type: string; lines: string[] }> = []
    const allLines: string[] = []
    let currentSection: { type: string; lines: string[] } | null = null

    // Process each paragraph
    editorState.root.children.forEach(paragraph => {
      if (paragraph.type === 'section-paragraph') {
        // Extract text from all child text nodes
        const lineText = paragraph.children
          .filter(child => child.type === 'text' || child.type === 'stressed-text')
          .map(child => child.text)
          .join('')
          .trim()

        if (lineText) {
          allLines.push(lineText)

          // Group by section type
          const sectionType = paragraph.sectionType || 'verse'

          if (!currentSection || currentSection.type !== sectionType) {
            // Start new section
            currentSection = { type: sectionType, lines: [] }
            sections.push(currentSection)
          }

          currentSection.lines.push(lineText)
        }
      }
    })

    // Create different text formats
    const plainText = allLines.join('\n')

    // Create structured text with section headers
    const structuredText = sections.map(section => {
      const sectionLabel = section.type.charAt(0).toUpperCase() + section.type.slice(1)
      return `[${sectionLabel}]\n${section.lines.join('\n')}`
    }).join('\n\n')

    return {
      sections,
      plainText,
      structuredText
    }
  } catch (error) {
    console.warn('Failed to parse Lexical data:', error)
    // Fallback to treating as plain text
    const text = typeof lexicalData === 'string' ? lexicalData : ''
    return {
      sections: [{ type: 'verse', lines: text.split('\n').filter(line => line.trim()) }],
      plainText: text,
      structuredText: text
    }
  }
}

/**
 * Get clean lyrics text for AI analysis (without stress patterns or metadata)
 */
export function getLyricsForAI(lexicalData: string | LexicalEditorState): string {
  const extracted = extractLyricsFromLexical(lexicalData)
  // For most AI analysis, structured text with section headers is best
  return extracted.structuredText || extracted.plainText
}

/**
 * Check if we should include stress patterns in AI analysis
 * For now, only include for prosody-specific analysis
 */
export function shouldIncludeStressPatterns(analysisType: string): boolean {
  return analysisType === 'prosody' || analysisType === 'comprehensive'
}

/**
 * Extract stress patterns if needed for prosody analysis
 */
export function extractStressPatternsFromLexical(lexicalData: string | LexicalEditorState): StressPatternData {
  try {
    const editorState = typeof lexicalData === 'string'
      ? JSON.parse(lexicalData) as LexicalEditorState
      : lexicalData

    const stressData: StressPatternData = {}

    editorState.root.children.forEach((paragraph, paragraphIndex) => {
      if (paragraph.type === 'section-paragraph') {
        paragraph.children.forEach(child => {
          const childNode = child as LexicalChildNode
          if (childNode.type === 'stressed-text' && childNode.stressPatterns) {
            stressData[`line_${paragraphIndex}`] = childNode.stressPatterns
          }
        })
      }
    })

    return stressData
  } catch (error) {
    console.warn('Failed to extract stress patterns:', error)
    return {}
  }
}
